import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../api/client';
import { LEGAL_ARTICLES, PASSWORD_MIN_LENGTH } from '@va/shared';
import { VAMark } from '../components/Brand';
import { LegalLink } from '../components/LegalLink';
import { PasswordInput } from '../components/PasswordInput';
import { Spinner } from '../components/ui';
import { isDesktop } from '../platform';
import { useAuth } from '../state/auth';

export function AuthShell({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="container auth-wrap">
      <div className="auth-card">
        <div className="card">
          <VAMark className="auth-mark" />
          <h2 className="center" style={{ marginBottom: 6 }}>{title}</h2>
          {subtitle && <p className="center muted" style={{ marginBottom: 22 }}>{subtitle}</p>}
          {children}
        </div>
        {footer && <p className="center small muted" style={{ marginTop: 18 }}>{footer}</p>}
      </div>
    </div>
  );
}

function useRedirectTarget(): string {
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from;
  return from && from.startsWith('/') && !from.startsWith('/login') ? from : '/';
}

export function LoginPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const target = useRedirectTarget();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaTicket, setMfaTicket] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (auth.user) nav(target, { replace: true });
  }, [auth.user, nav, target]);
  useEffect(() => {
    if (mfaTicket) codeRef.current?.focus();
  }, [mfaTicket]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaTicket) {
        await auth.loginMfa(mfaTicket, code);
      } else {
        const r = await auth.login(email, password);
        if (!r.ok) setMfaTicket(r.mfaTicket);
      }
    } catch (err) {
      setError(errorMessage(err));
      if (mfaTicket) setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (mfaTicket) {
    return (
      <AuthShell title="Two-step verification" subtitle="Enter the 6-digit code from your authenticator app.">
        <form className="stack" onSubmit={submit}>
          {error && <div className="form-error">{error}</div>}
          <input
            ref={codeRef}
            className="input mono center"
            style={{ fontSize: 24, letterSpacing: '0.4em' }}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            aria-label="Verification code"
          />
          <button className="btn btn-primary btn-lg btn-block" disabled={busy || code.length !== 6}>
            {busy ? <Spinner /> : 'Verify'}
          </button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setMfaTicket(null)}>
            Use a different account
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle={isDesktop ? 'Sign in to unlock your plugins on this computer.' : 'Welcome back to the VA Plugin Store.'}
      footer={
        <>
          New here? <Link to="/register" state={{ from: target }}>Create an account</Link>
        </>
      }
    >
      <form className="stack" onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <PasswordInput id="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="row between small">
          <span />
          <Link to="/forgot">Forgot your password?</Link>
        </div>
        <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? <Spinner /> : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const auth = useAuth();
  const nav = useNavigate();
  const target = useRedirectTarget();
  const [form, setForm] = useState({ displayName: '', email: '', password: '', acceptTerms: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (auth.user) nav(target, { replace: true });
  }, [auth.user, nav, target]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.register({ email: form.email, password: form.password, displayName: form.displayName || undefined, acceptTerms: form.acceptTerms });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="One account for every plugin you buy, on all your computers."
      footer={
        <>
          Already have an account? <Link to="/login" state={{ from: target }}>Sign in</Link>
        </>
      }
    >
      <form className="stack" onSubmit={submit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" className="input" autoComplete="name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Optional" />
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" className="input" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <PasswordInput id="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <span className="hint">At least {PASSWORD_MIN_LENGTH} characters. Words of your own are best.</span>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={form.acceptTerms} onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })} />
          <span>
            I accept the <LegalLink slug={LEGAL_ARTICLES.terms}>terms of sale</LegalLink> and the{' '}
            <LegalLink slug={LEGAL_ARTICLES.license}>license agreement</LegalLink>, and I've read the{' '}
            <LegalLink slug={LEGAL_ARTICLES.privacy}>privacy policy</LegalLink>.
          </span>
        </label>
        <button className="btn btn-primary btn-lg btn-block" disabled={busy || !form.acceptTerms}>
          {busy ? <Spinner /> : 'Create account'}
        </button>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a link to choose a new one." footer={<Link to="/login">Back to sign in</Link>}>
      {sent ? (
        <div className="form-ok">If an account exists for {email}, a reset link is on its way. Check your inbox (and spam folder).</div>
      ) : (
        <form className="stack" onSubmit={submit}>
          {error && <div className="form-error">{error}</div>}
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-lg btn-block" disabled={busy}>{busy ? <Spinner /> : 'Send reset link'}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Choose a new password">
      {done ? (
        <div className="stack">
          <div className="form-ok">Your password was changed and you were signed out everywhere.</div>
          <Link className="btn btn-primary btn-block" to="/login">Sign in</Link>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          {error && <div className="form-error">{error}</div>}
          <div className="field">
            <label htmlFor="password">New password</label>
            <PasswordInput id="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="hint">At least {PASSWORD_MIN_LENGTH} characters. Words of your own are best.</span>
          </div>
          <button className="btn btn-primary btn-lg btn-block" disabled={busy || !token}>{busy ? <Spinner /> : 'Save password'}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, setUser } = useAuth();
  const [state, setState] = useState<'working' | 'ok' | 'error'>('working');
  const [error, setError] = useState('');
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('error');
      setError('This link is missing its token.');
      return;
    }
    api
      .post('/auth/verify-email', { token })
      .then(() => {
        setState('ok');
        if (user) setUser({ ...user, emailVerified: true });
      })
      .catch((err) => {
        setState('error');
        setError(errorMessage(err));
      });
  }, [token, user, setUser]);
  return (
    <AuthShell title="Email confirmation">
      {state === 'working' && <div className="center"><Spinner /></div>}
      {state === 'ok' && (
        <div className="stack">
          <div className="form-ok">Thanks! Your email address is confirmed.</div>
          <Link className="btn btn-primary btn-block" to="/">Go to the store</Link>
        </div>
      )}
      {state === 'error' && <div className="form-error">{error}</div>}
    </AuthShell>
  );
}
